import { Alert, Collapse, Modal, Spin, Table, Typography } from "antd";
import { useSmartgrdEmonPowerStatusSubscription } from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useMemo } from "react";

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface CTMeasurementsModalProps {
  visible: boolean;
  onClose: () => void;
  meteringPointPositionId: string | null;
  assetName?: string;
}

const CTMeasurementsModal: React.FC<CTMeasurementsModalProps> = ({
  visible,
  onClose,
  meteringPointPositionId,
  assetName,
}) => {
  const { data, loading, error } = useSmartgrdEmonPowerStatusSubscription({
    variables: { meteringPointPositionId: meteringPointPositionId! },
    skip: !meteringPointPositionId || !visible,
  });

  const columns = [
    {
      title: "Channel",
      dataIndex: "channelIndex",
      key: "channelIndex",
      render: (channelIndex: number) => `Channel ${channelIndex}`,
    },
    {
      title: "Measurement Value",
      dataIndex: "lastPowerReading",
      key: "lastPowerReading",
      render: (value: number, record: any) => {
        if (value === null || value === undefined) return "No data";

        const numValue = Number(value).toFixed(2);
        const measurementType = record.measurementType?.toLowerCase();

        let unit = "W"; // default to W
        if (measurementType === "energy" || measurementType === "consumed_energy") {
          unit = "kWh";
        } else if (measurementType === "power") {
          unit = "W";
        }

        return `${numValue} ${unit}`;
      },
    },
    {
      title: "Measurement Type",
      dataIndex: "measurementType",
      key: "measurementType",
    },
    {
      title: "Last Updated",
      dataIndex: "mappedTime",
      key: "mappedTime",
      render: (mappedTime: string) => {
        if (!mappedTime) return "No data";
        return new Date(mappedTime).toLocaleString();
      },
    },
  ];

  const measurements = useMemo(() => {
    return data?.smartgrdEmonPowerStatus || [];
  }, [data]);

  // Group measurements by nodeMacId
  const groupedMeasurements = useMemo(() => {
    const groups = new Map<string, any[]>();

    measurements.forEach((measurement) => {
      const nodeId = measurement.nodeMacId || "Unknown Node";
      if (!groups.has(nodeId)) {
        groups.set(nodeId, []);
      }
      groups.get(nodeId)!.push(measurement);
    });

    return groups;
  }, [measurements]);

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            CT Node Measurements
          </Title>
          {assetName && (
            <Text type="secondary" style={{ fontSize: "14px" }}>
              {assetName}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <Spin spinning={loading} tip="Loading measurements...">
        {error && (
          <Alert
            message="Error loading measurements"
            description={error.message}
            type="error"
            style={{ marginBottom: 16 }}
          />
        )}

        {!loading && !error && measurements.length === 0 && (
          <Alert
            message="No measurements available"
            description="No measurement data found for this CT node."
            type="info"
            style={{ marginBottom: 16 }}
          />
        )}

        {measurements.length > 0 && (
          <>
            {groupedMeasurements.size > 1 ? (
              // Multiple nodes - use collapsible panels
              <Collapse defaultActiveKey={Array.from(groupedMeasurements.keys())}>
                {Array.from(groupedMeasurements.entries()).map(([nodeId, nodeMeasurements]) => (
                  <Panel
                    header={
                      <div>
                        <Text strong>Node: </Text>
                        <Text code style={{ fontSize: "12px" }}>
                          {nodeId}
                        </Text>
                        <Text style={{ marginLeft: 16, color: "#666" }}>({nodeMeasurements.length} measurements)</Text>
                      </div>
                    }
                    key={nodeId}
                  >
                    <Table
                      columns={columns}
                      dataSource={nodeMeasurements}
                      rowKey={(record) => `${record.subjectId}-${record.channelIndex}-${record.measurementType}`}
                      pagination={false}
                      size="small"
                      bordered
                    />
                  </Panel>
                ))}
              </Collapse>
            ) : (
              // Single node - show table directly
              <div>
                {Array.from(groupedMeasurements.entries()).map(([nodeId, nodeMeasurements]) => (
                  <div key={nodeId}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Node MAC ID: </Text>
                      <Text code style={{ fontSize: "12px" }}>
                        {nodeId}
                      </Text>
                    </div>
                    <Table
                      columns={columns}
                      dataSource={nodeMeasurements}
                      rowKey={(record) => `${record.subjectId}-${record.channelIndex}-${record.measurementType}`}
                      pagination={false}
                      size="small"
                      bordered
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default CTMeasurementsModal;
